import { useState, Fragment } from "react";
import { Lock, Unlock, Trash2, Plus, Check, X } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";
import { fmtInt } from "@/lib/api";

const LABEL_TD = "px-4 py-2 font-display uppercase text-xs tracking-widest sticky left-0 bg-card z-10 border-r-2 border-foreground/20";
const SUB_TD = "pl-8 pr-4 py-1 sticky left-0 bg-card z-10 border-r-2 border-foreground/20 font-mono-num text-muted-foreground";

function Static({ v, strong, muted, testid }) {
  const neg = v !== null && v < 0;
  return (
    <span data-testid={testid}
      className={`font-mono-num ${strong ? "font-bold text-primary" : muted ? "text-muted-foreground" : ""} ${neg && !strong ? "text-destructive" : ""}`}>
      {v === null ? "—" : fmtInt(v)}
    </span>
  );
}

export function BudgetMatrixTable({ t, rows, histCols, budgetCols, clubLocked, onCommitField, onCommitSubpost, onToggleLock, onDelete, onAddSubpost }) {
  const [addingCat, setAddingCat] = useState(null);
  const [newName, setNewName] = useState("");
  const submitAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    await onAddSubpost(addingCat, name);
    setAddingCat(null); setNewName("");
  };
  const colSpan = 1 + histCols.length + budgetCols.length;
  const editable = (row) => row.kind === "agg" || row.kind === "sub" || row.kind === "field";
  const initialFor = (row, b) => (row.kind === "sub" ? (row.value(b) ?? 0) : row.kind === "agg" && row.field === "revenue" ? row.value(b) : Number(b[row.field]) || 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="budget-table">
        <thead>
          <tr className="bg-muted/40 border-b-2 border-foreground">
            <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs sticky left-0 bg-muted/40 z-10 min-w-[150px] md:min-w-[220px] border-r-2 border-foreground/20">{t("Post")}</th>
            {histCols.map((h) => (
              <th key={`h-${h.id}`} data-testid={`budget-hist-header-${h.year}`}
                className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs min-w-[150px] bg-muted/60 text-muted-foreground">
                <div>{t("Resultaat")} {h.year}</div>
                <div className="text-[9px] normal-case tracking-normal font-normal">{t("jaarrekening · alleen lezen")}</div>
              </th>
            ))}
            {budgetCols.map((b) => (
              <th key={b.id} data-testid={`budget-header-${b.year}`}
                className="text-right px-3 py-2 font-display uppercase tracking-widest text-xs font-black min-w-[170px]">
                <div className="flex items-center justify-end gap-1">
                  <span className="flex items-center gap-1">{t("Begroting")} {b.year}
                    {b.locked && <Lock className="w-3 h-3 text-primary" data-testid={`budget-lock-icon-${b.year}`} />}
                  </span>
                  {!clubLocked && (
                    <>
                      <button onClick={() => onToggleLock(b)} title={b.locked ? t("Ontgrendelen") : t("Vergrendelen")}
                        data-testid={`budget-lock-${b.year}`} className="hover:text-primary p-1">
                        {b.locked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </button>
                      <button onClick={() => onDelete(b)} title={t("Verwijderen")} disabled={b.locked}
                        data-testid={`budget-delete-${b.year}`} className="hover:text-destructive p-1 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Fragment key={row.key}>
              <tr data-testid={`budget-row-${row.key}`}
                className={row.kind === "computed" ? "bg-primary/10 font-bold" : row.kind === "agg" ? "bg-muted/30 font-semibold" : row.kind === "sys" ? "italic" : "hover:bg-muted/40"}>
                {row.kind === "sub" || row.kind === "sys" ? (
                  <td className={SUB_TD} title={row.kind === "sys" ? t("Automatisch berekend: totaal − som van de subposten. Negatief = de subposten tellen op tot meer dan het totaal.") : undefined}>
                    <span className={row.kind === "sys" ? "italic" : ""}>{row.label}</span>
                    {row.kind === "sys" && <span className="ml-2 text-[9px] font-display uppercase tracking-widest text-muted-foreground/50" title={t("Systeem-subpost — automatisch berekend als residu")}>{t("SYS")}</span>}
                  </td>
                ) : (
                  <td className={LABEL_TD}>{row.label}</td>
                )}
                {histCols.map((h) => (
                  <td key={`h-${h.id}`} className="px-3 py-1 text-right bg-muted/20">
                    <Static v={row.value(h)} strong={row.strong} muted testid={`budget-hist-cell-${row.key}-${h.year}`} />
                  </td>
                ))}
                {budgetCols.map((b) => {
                  const ro = clubLocked || b.locked;
                  return (
                    <td key={b.id} className="px-2 py-1 text-right">
                      {editable(row) && !ro ? (
                        <MoneyInput
                          key={`${row.key}-${b.id}-${initialFor(row, b)}`}
                          value={initialFor(row, b)}
                          onCommit={(v) => (row.kind === "sub" ? onCommitSubpost(b, row.cat, row.name, v) : onCommitField(b, row.field, v))}
                          testid={`budget-cell-${row.key}-${b.year}`}
                          className={`w-full bg-transparent px-2 py-1 font-mono-num text-right focus:outline-none focus:bg-primary/10 ${row.kind === "sub" ? "text-muted-foreground" : "font-semibold"}`}
                        />
                      ) : (
                        <Static v={row.value(b)} strong={row.strong} muted={row.kind === "sub" || row.kind === "sys"} testid={`budget-cell-${row.key}-${b.year}`} />
                      )}
                    </td>
                  );
                })}
              </tr>
              {row.addCat && !clubLocked && (
                <tr>
                  <td colSpan={colSpan} className="pl-4 pr-4 py-1 sticky left-0 z-10 border-b border-dashed border-foreground/20">
                    {addingCat === row.addCat ? (
                      <div className="flex items-center gap-1 max-w-md">
                        <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitAdd(); } else if (e.key === "Escape") { setAddingCat(null); setNewName(""); } }}
                          placeholder={t("Naam nieuwe subpost")} data-testid={`budget-newsp-name-${row.addCat}`}
                          className="flex-1 border-2 border-foreground bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                        <button onClick={submitAdd} data-testid={`budget-newsp-save-${row.addCat}`} className="text-primary" title={t("Opslaan")}><Check className="w-4 h-4" /></button>
                        <button onClick={() => { setAddingCat(null); setNewName(""); }} className="text-muted-foreground" title={t("Annuleren")}><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <button onClick={() => setAddingCat(row.addCat)} data-testid={`budget-newsp-btn-${row.addCat}`}
                        className="text-primary hover:text-primary/80 inline-flex items-center gap-1 font-display uppercase tracking-widest text-[11px] font-bold">
                        <Plus className="w-3 h-3" /> {t("Subpost toevoegen")}
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
